import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { Advertiser } from 'core/advertiser/Advertiser';
import {
  DefaultAdvertiserManager,
  AdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import i18n from 'i18n';
import {
  AdvertiserMemberListModel,
  DefaultAdvertiserMemberListModel
} from 'components/AdvertiserMemberList/AdvertiserMemberListModel';
import _ from 'lodash';
import { CompanyMember } from 'core';
import { renderOfficialWebsite } from './AdvertiserDetailRenderFunctions';
import { SyntheticEvent } from 'react';

export enum ListType {
  MEMBER = 'member',
  BEACON = 'beacon'
}

export interface AdvertiserDetailModel {
  advertiser?: Advertiser;
  readonly advertiserId: string | number;
  readonly state: AdvertiserDetailState;
  readonly event: UpdateEventListener<AdvertiserDetailModel>;
  init(): Promise<void>;
  onUnmount(eventHandler?: number): void;
  getAdvertiserMemberListModel(): AdvertiserMemberListModel;
  switchList(listType: string | null, e: SyntheticEvent<unknown, Event>): void;
}

export type AdvertiserDetailProps = {
  readonly model: AdvertiserDetailModel;
};

export type AdvertiserDetailState = {
  readonly loading: boolean;
  readonly advertiserViewData: any;
  readonly listType: ListType;
  readonly members: CompanyMember[];
};

export class DefaultAdvertiserDetailModel implements AdvertiserDetailModel {
  advertiser?: Advertiser;
  advertiserId: string | number;
  event: FireableUpdateEventListener<AdvertiserDetailModel>;
  manager: AdvertiserManager;
  handler?: number;
  advertiserViewData: any;
  loading: boolean;
  advertiserMemberListModel?: AdvertiserMemberListModel;
  listType: ListType;
  members: CompanyMember[];

  constructor(
    advertiserId: string | number,
    manager: AdvertiserManager = new DefaultAdvertiserManager()
  ) {
    this.advertiserId = advertiserId;
    this.manager = manager;
    this.event = new FireableUpdateEventListener<AdvertiserDetailModel>();
    this.loading = false;
    this.members = [];
    this.listType = ListType.MEMBER;
  }

  get state(): AdvertiserDetailState {
    return {
      advertiserViewData: this.advertiserViewData,
      loading: this.loading,
      listType: this.listType,
      members: this.members
    };
  }

  onUnmount(eventHandler?: number) {
    this.advertiser = undefined;
    eventHandler !== undefined && this.event.remove(eventHandler);
  }

  getAdvertiserMemberListModel() {
    if (
      this.advertiserMemberListModel &&
      this.advertiserMemberListModel.members === this.members
    ) {
      return this.advertiserMemberListModel;
    }
    this.advertiserMemberListModel = new DefaultAdvertiserMemberListModel(
      this.advertiserId,
      this.members,
      this.refreshMember
    );
    return this.advertiserMemberListModel;
  }

  async init(): Promise<void> {
    this.updateState(true);
    try {
      this.advertiser = await this.manager.getAdvertiser(this.advertiserId);
      this.members = await this.manager.getAdvertiserMembers(this.advertiserId);
      this.genAdvertiserViewData();
    } catch (error) {}
    this.updateState(false);
  }

  refreshMember = async (): Promise<void> => {
    this.updateState(true);
    try {
      this.members = await this.manager.getAdvertiserMembers(this.advertiserId);
    } catch (error) {}
    this.updateState(false);
  };

  genAdvertiserViewData() {
    const advertiser = this.advertiser;
    if (!advertiser) {
      this.advertiserViewData = {};
      return;
    }
    this.advertiserViewData = _.omitBy(
      {
        basic: _.omitBy(
          {
            id: advertiser.id,
            category: advertiser.category
              ? i18n.t(
                  `tenmaxCategory.labels.${advertiser.category.toLowerCase().replace('-', '_')}`
                )
              : i18n.t('campaignList.labels.emptyContent'),
            note: advertiser.note
              ? advertiser.note
              : i18n.t('campaignList.labels.emptyContent'),
            website: renderOfficialWebsite(advertiser.website),
            publisher:
              advertiser.limitPublisher.length > 0
                ? advertiser.limitPublisher.join(',')
                : undefined
          },
          _.isUndefined
        ),
        other:
          advertiser.shareSegment && advertiser.shareSegment.length > 0
            ? {
                segmentSource: advertiser.shareSegment
                  .map(
                    (source) =>
                      `${source.agencyName} / ${source.advertiserName}`
                  )
                  .join(', ')
              }
            : undefined
      },
      _.isUndefined
    );
  }

  switchList = (listType: string | null, e: SyntheticEvent<unknown, Event>) => {
    e && e.stopPropagation();
    if (listType === null) {
      return;
    }
    this.listType = listType as ListType;
    this.updateState(false);
  };

  updateState(loading = false) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
